import React, { useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { X, Download, Printer } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const partyLabel = (party) => {
  if (party === 'shipper') return 'Représentant expéditeur';
  if (party === 'consignee') return 'Représentant destinataire';
  if (party === 'driver') return 'Chauffeur Baobab';
  if (party === 'observer') return 'Opérateur Baobab';
  return party;
};

/**
 * Modal QR code pour les liens de signature.
 * PM Spec 07/05/2026 sprint 2 — Permet à l'opérateur Baobab d'afficher un QR
 * sur sa tablette/téléphone pour que le chauffeur ou le représentant scanne
 * et accède directement à sa page de signature personnalisée.
 */
const SignatureLinkQRModal = ({ link, note, onClose, onShown }) => {
  const canvasRef = useRef(null);

  const handleDownload = () => {
    const canvas = document.querySelector('[data-qr-canvas] canvas');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `qr-${note?.noteNumber || 'bon'}-${link.party}.png`;
    a.click();
    onShown?.('qr');
  };

  const handlePrint = () => {
    const canvas = document.querySelector('[data-qr-canvas] canvas');
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    const w = window.open('', '_blank', 'width=520,height=720');
    if (!w) return;
    const ref = note?.operationReference || note?.noteNumber || '';
    w.document.write(`
      <html><head><title>QR Signature ${ref}</title></head>
      <body style="font-family:Arial,sans-serif;text-align:center;padding:20px;">
        <h2 style="color:#0f766e;margin:0 0 4px;">Baobab Connect</h2>
        <div style="font-size:14px;color:#475569;margin-bottom:12px;">
          ${note?.type === 'loading' ? 'Bon de chargement' : 'Bon de déchargement'} — ${ref}
        </div>
        <div style="font-size:13px;font-weight:600;color:#9a3412;margin-bottom:16px;">
          Signature : ${partyLabel(link.party)}${link.targetName ? ' (' + link.targetName + ')' : ''}
        </div>
        <img src="${dataUrl}" style="width:300px;height:300px;border:1px solid #e2e8f0;" />
        <div style="font-size:11px;color:#64748b;margin-top:16px;max-width:300px;margin-left:auto;margin-right:auto;">
          Scannez ce QR avec votre téléphone pour accéder directement à la page de signature.
          <br/><br/>Code de secours : <b style="font-size:14px;letter-spacing:2px;">${link.code}</b>
        </div>
      </body></html>
    `);
    w.document.close();
    setTimeout(() => { w.print(); }, 400);
    onShown?.('qr');
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="qr-modal">
      <Card className="w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-base font-semibold text-slate-800">QR de signature</div>
              <div className="text-xs text-slate-500 mt-0.5">
                Le destinataire scanne et accède directement à sa page.
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0">
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="flex flex-col items-center gap-2 py-2">
            <Badge className="bg-amber-100 text-amber-700 border-amber-200">
              {partyLabel(link.party)}{link.targetName ? ` · ${link.targetName}` : ''}
            </Badge>
            <div data-qr-canvas
                 className="p-3 border-2 border-slate-200 rounded-lg bg-white">
              <QRCodeCanvas
                ref={canvasRef}
                value={link.url}
                size={240}
                level="M"
                includeMargin={false}
                fgColor="#0f172a"
                bgColor="#ffffff"
              />
            </div>
            <div className="text-[11px] text-slate-500 text-center max-w-[240px]">
              Code de secours : <span className="font-mono font-semibold tracking-widest">{link.code}</span>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1"
                    onClick={handleDownload}
                    data-testid="qr-download-btn">
              <Download className="w-3.5 h-3.5 mr-1.5" /> PNG
            </Button>
            <Button variant="outline" size="sm" className="flex-1"
                    onClick={handlePrint}
                    data-testid="qr-print-btn">
              <Printer className="w-3.5 h-3.5 mr-1.5" /> Imprimer
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SignatureLinkQRModal;
